import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DISPLAY_NAME_MAX } from "@badorbad/shared";
import { AvatarPicker } from "../design/AvatarPicker";
import { randomSeed } from "../design/seed";
import { t, fmt } from "../locale";
import { useAuth } from "../stores/auth";
import { useGame } from "../stores/game";

export const JoinFlow = () => {
  const [, navigate] = useLocation();
  const savedName = useAuth((s) => s.displayName);
  const savedSeed = useAuth((s) => s.avatarSeed);

  const [name, setName] = useState(savedName ?? "");
  const [seed, setSeed] = useState(savedSeed ?? randomSeed);
  const [error, setError] = useState<string | null>(null);

  const join = useGame((s) => s.join);
  const resume = useGame((s) => s.resume);
  const resumePending = useGame((s) => s.resumePending);
  const connecting = useGame((s) => s.connecting);
  const wsError = useGame((s) => s.error);
  const phase = useGame((s) => s.phase);
  const pendingCode = useGame((s) => s.pendingCode);
  const resumeAttempted = useRef(false);

  // Navigating during render updates the router — and therefore Landing —
  // while this component is still rendering, which React reports as a
  // cross-component setState. Both redirects belong in an effect.
  const leaving = pendingCode === null;
  const joined = phase !== null;

  useEffect(() => {
    if (leaving) navigate("/", { replace: true });
    else if (joined) navigate("/room", { replace: true });
  }, [leaving, joined, navigate]);

  useEffect(() => {
    if (!pendingCode || joined || resumeAttempted.current) return;
    resumeAttempted.current = true;
    void resume();
  }, [pendingCode, joined, resume]);

  if (leaving || joined) return null;

  if (pendingCode && (!resumeAttempted.current || resumePending)) {
    return (
      <div className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center px-5 text-center">
        <p className="text-sm text-ink-dim">{t.connection.reconnecting}</p>
      </div>
    );
  }

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t.name.prompt);
      return;
    }
    if (trimmed.length > DISPLAY_NAME_MAX) {
      setError(fmt(t.name.tooLong, DISPLAY_NAME_MAX));
      return;
    }
    setError(null);
    await join(trimmed, seed);
  };

  const displayError = error ?? wsError;

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center px-5">
      <div className="flex w-full flex-col items-center gap-6">
        <AvatarPicker initialSeed={seed} onChange={setSeed} />

        <p className="text-sm text-ink-dim">{t.name.prompt}</p>

        <input
          className="w-full bg-surface px-4 py-3 text-center text-base text-ink placeholder:text-ink-dim"
          placeholder={t.name.placeholder}
          maxLength={DISPLAY_NAME_MAX}
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
        />

        <button
          className="w-full bg-side-a px-4 py-4 text-base font-bold uppercase tracking-tight text-bg"
          disabled={connecting || !name.trim()}
          onClick={handleSubmit}
        >
          {connecting
            ? "..."
            : pendingCode
              ? t.landing.joinRoom
              : t.landing.createRoom}
        </button>

        {displayError && (
          <p className="text-center text-sm text-side-b">{displayError}</p>
        )}
      </div>
    </div>
  );
};
